import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

interface ThemeContextValue {
  theme: string;
  setTheme: (theme: string) => void;
  toggleTheme?: () => void;
}

// Create context for theme management
const ThemeProviderContext = createContext<ThemeContextValue>({ theme: 'light', setTheme: () => null });

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: string;
  storageKey?: string;
}

/**
 * ThemeProvider component for managing application theming
 * Handles theme persistence and system preference detection
 */
export function ThemeProvider({
  children,
  defaultTheme = 'light',
  storageKey = 'fairdee-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<string>(() => {
    try {
      // Try to get theme from local storage
      const storedTheme = localStorage.getItem(storageKey);
      if (storedTheme) {
        return storedTheme;
      }

      // Default to light if no stored theme or system preference
      return defaultTheme;
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      return defaultTheme;
    }
  });

  // Update local storage when theme changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, theme);

      // Apply class to document element
      const root = window.document.documentElement;

      // Remove all theme classes
      root.classList.remove('light', 'dark');

      // Add the current theme class
      root.classList.add(theme);
    } catch (error) {
      console.error('Error setting theme:', error);
    }
  }, [storageKey, theme]);

  // Handle system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      // Only change theme if user hasn't manually set it
      if (!localStorage.getItem(storageKey)) {
        setTheme(mediaQuery.matches ? 'dark' : 'light');
      }
    };

    // Add event listener
    mediaQuery.addEventListener('change', handleChange);

    // Clean up
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [storageKey]);

  const value: ThemeContextValue = {
    theme,
    setTheme: (newTheme: string) => {
      setTheme(newTheme);
    },
    toggleTheme: () => {
      setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

/**
 * Hook for using the theme context
 */
export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};
